import React from 'react';
import {
  FormControl,
  FormErrorMessage,
  useTheme,
} from '@wegroup/design-system';
import useFlowInputComponent from '../../../../hooks/useFlowsInputComponent';
import { FlowsQuestion } from '../../../../types/FlowsTypes';
import { DatePickerInput, DatesProvider } from '@mantine/dates';
import { useLocale } from '../../../../../../../i18n';
import 'dayjs/locale/en';
import 'dayjs/locale/nl';
import 'dayjs/locale/fr';
import { format, isValid, parse, parseISO } from 'date-fns';
// import { utcToZonedTime } from 'date-fns-tz';

interface Props {
  question: FlowsQuestion;
}

const flowsInputFieldStyling = {
  bg: 'white',
  height: '4.5rem',
  boxShadow: 'base',
  border: '1px',
  fontSize: 'md',
  borderRadius: 'md',
  borderColor: '#e4e4e4;',
  _focus: {
    border: '1px',
    borderColor: 'primary.500',
  },
  _invalid: {
    border: '1px',
    borderColor: 'danger.main',
  },
  _disabled: {
    cursor: 'not-allowed',
    pointerEvents: 'none',
    opacity: '0.4',
  },
};

const dateFormat = 'yyyy-MM-dd';

const DateInputPickerComponent: React.FC<Props> = ({ question }) => {
  const {
    field: { name, value, onBlur },
    error,
    prefilled,
  } = useFlowInputComponent({
    question,
  });

  const { userLang } = useLocale();
  const { colors, zIndices } = useTheme();
  // const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const minDateParsed = question.rules.ge
    ? parse(question.rules.ge as string, dateFormat, new Date())
    : undefined;
  const minDate = minDateParsed
    ? isValid(minDateParsed)
      ? minDateParsed
      : parseISO(question.rules.ge as string)
    : null;
  const maxDateParsed = question.rules.le
    ? parse(question.rules.le as string, dateFormat, new Date())
    : undefined;
  const maxDate = maxDateParsed
    ? isValid(maxDateParsed)
      ? maxDateParsed
      : parseISO(question.rules.le as string)
    : null;
  const parsedDate = value ? parse(value, dateFormat, new Date()) : null;
  const dateValue = parsedDate
    ? isValid(parsedDate)
      ? parsedDate
      : parseISO(value)
    : null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const newStyles = {
    input: {
      backgroundColor: prefilled ? colors.prefilled.backgroundColor : 'white',
      borderColor: prefilled ? colors.prefilled.borderColor : '#e4e4e4;',
    },
  };

  const handleChange = (value: Date | null) => {
    onBlur({
      name: question.id,
      value: value ? format(value, dateFormat) : null,
    });
  };

  const isBirth = name.includes('birth');

  return (
    <FormControl
      isInvalid={!!error}
      sx={{ '.mantine-DatePickerInput-input': flowsInputFieldStyling }}
    >
      <DatesProvider
        settings={{
          locale: userLang,
        }}
      >
        <DatePickerInput
          placeholder="DD/MM/YYYY"
          styles={newStyles}
          value={dateValue}
          minDate={minDate || undefined}
          maxDate={maxDate || undefined}
          popoverProps={{
            withinPortal: true,
            zIndex:
              zIndices['dropdownHigh' as keyof typeof zIndices] || 9999999,
          }}
          onChange={handleChange}
          disabled={question.rules.is_disabled}
          defaultDate={isBirth && maxDate && !dateValue ? maxDate : undefined}
          defaultLevel={isBirth && !dateValue ? 'decade' : undefined}
          clearable
          hideOutsideDates
        />
      </DatesProvider>
      {error?.message && <FormErrorMessage>{error.message}</FormErrorMessage>}
    </FormControl>
  );
};

export default DateInputPickerComponent;
